import axios, {AxiosError, AxiosInstance} from 'axios';
import {AxiosExtendsRequest} from 'models/http-interface';
import {updateAccessTokenAsync} from 'redux/auth/thunk';
import {store} from 'redux/store';
import {HTTP_CODE} from 'utils/http-code';
import {getKey} from 'utils/local-storage';
import {removeUser} from 'utils/remove-user';
import {env} from 'config/env';
import {API_TIMEOUTS} from './../constants/axios-timeout';

const {DEFAULT} = API_TIMEOUTS;

const createAxiosInstance = (baseURL?: string): AxiosInstance => {
  const instance: AxiosInstance = axios.create({
    baseURL,
    timeout: DEFAULT,
  });

  /* The request interceptor attaches auth and lang params to every request */
  instance.interceptors.request.use(
    req => {
      const token = store.getState().auth.accessToken;
      if (token) {
        req.headers['Authorization'] = `Bearer ${token}`;
      }
      req.params = {
        ...req.params,
        lang: getKey('i18nextLng'),
      };

      return req;
    },
    (error: AxiosError) => Promise.reject(error.message),
  );

  /* The response interceptor handles auth refresh and network errors */
  instance.interceptors.response.use(
    response => response,
    async (error: AxiosError) => {
      const originalRequest: AxiosExtendsRequest = error?.config;
      if (error.response) {
        if (error.response.status === 401) {
          removeUser();
        }

        if (error.response.status === 412 && originalRequest && !originalRequest?._retry) {
          originalRequest._retry = true;

          const refreshToken = store.getState().auth.refreshToken;
          const response = await store.dispatch(updateAccessTokenAsync({refreshToken})).unwrap();

          if (response) {
            return instance(originalRequest);
          }
          return Promise.reject(error.response.data);
        }

        return Promise.reject(error.response.data);
      }

      if (error.code === HTTP_CODE.ECONNABORTED) {
        return Promise.reject({error: 'Internal Server Error'});
      }
      return Promise.reject({error: error.message});
    },
  );

  return instance;
};

const axiosInstance = createAxiosInstance(env.backendUrl);
const axiosInstanceV2 = createAxiosInstance(env.backendUrlV2);

export {axiosInstanceV2};
export const V2ApiClient = axiosInstanceV2;
export default axiosInstance;
